import { useState } from 'react';

interface StarRatingProps {
  value: number | null | undefined;
  onChange?: (v: number) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  readOnly?: boolean;
  color?: 'amber' | 'vision';
  title?: string;
}

/**
 * Widget de 1-5 estrellas. Si `onChange` viene, es editable; si no, read-only.
 * Usado en:
 *  - Preview popup del asset (operador califica)
 *  - Chip chico en el grid de referencias
 *  - Modal "📜 Prompts" para mostrar rating del AI y del user por cada output
 */
export function StarRating({
  value,
  onChange,
  size = 'md',
  label,
  readOnly,
  color = 'amber',
  title,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const editable = !readOnly && !!onChange;
  const current = hover ?? value ?? 0;

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-base',
    lg: 'text-xl',
  }[size];

  const activeColor = color === 'vision' ? 'text-vision' : 'text-amber-400';

  return (
    <div className="inline-flex items-center gap-1" title={title}>
      {label && <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>}
      <div
        className={`inline-flex ${sizeClasses} leading-none`}
        onMouseLeave={() => setHover(null)}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= current;
          return (
            <button
              key={n}
              type="button"
              disabled={!editable}
              onClick={() => onChange?.(n)}
              onMouseEnter={() => editable && setHover(n)}
              className={`${
                editable ? 'cursor-pointer hover:scale-110' : 'cursor-default'
              } transition-transform px-0.5 ${filled ? activeColor : 'text-slate-600'}`}
              aria-label={`${n} de 5`}
            >
              {filled ? '★' : '☆'}
            </button>
          );
        })}
      </div>
    </div>
  );
}
